'use client';

import { motion, useScroll, useTransform } from 'framer-motion';
import { Clock, Euro, TrendingDown, Zap } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { MouseEvent, useRef } from 'react';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

import { useSectionVisibility } from '../../hooks/useSectionVisibility';

interface BenefitItem {
  key: string;
  icon: React.ComponentType<{ className?: string }>;
  spotlightColor: string;
  iconColor: string;
  iconBg: string;
  gradientFrom: string;
  gradientTo: string;
}

const benefits: BenefitItem[] = [
  {
    key: 'item1',
    icon: TrendingDown,
    spotlightColor: '#10b981',
    iconColor: 'text-emerald-500',
    iconBg: 'bg-emerald-500/10',
    gradientFrom: 'from-emerald-500/10',
    gradientTo: 'to-emerald-500/5',
  },
  {
    key: 'item2',
    icon: Euro,
    spotlightColor: '#3b82f6',
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-500/10',
    gradientFrom: 'from-blue-500/10',
    gradientTo: 'to-blue-500/5',
  },
  {
    key: 'item3',
    icon: Clock,
    spotlightColor: '#8b5cf6',
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-500/10',
    gradientFrom: 'from-purple-500/10',
    gradientTo: 'to-purple-500/5',
  },
  {
    key: 'item4',
    icon: Zap,
    spotlightColor: '#f59e0b',
    iconColor: 'text-amber-500',
    iconBg: 'bg-amber-500/10',
    gradientFrom: 'from-amber-500/10',
    gradientTo: 'to-amber-500/5',
  },
];

interface BenefitsSectionProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;
}

// Alternative Modern 3D Variant
export function BenefitsSection({ className, ...props }: BenefitsSectionProps) {
  const t = useTranslations('landing.benefits');
  const containerRef = useRef<HTMLElement>(null);
  const { ref: visibilityRef } = useSectionVisibility('benefits');

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start end', 'end start'],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  return (
    <section
      id="benefits"
      ref={(el) => {
        (containerRef as React.MutableRefObject<HTMLElement | null>).current = el;
        (visibilityRef as React.MutableRefObject<HTMLElement | null>).current = el;
      }}
      className="relative bg-muted/30 overflow-hidden"
    >
      {/* Background Decorations */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-96 h-96 bg-primary/5 rounded-full blur-[128px]" />
        <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-blue-500/5 rounded-full blur-[128px]" />
      </div>

      <div
        className={cn(
          'container mx-auto px-4 py-16 sm:px-6 md:py-24 lg:px-8 relative z-10',
          className,
        )}
        {...props}
      >
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.header
            style={{ opacity }}
            className="max-w-3xl mx-auto text-center mb-16 space-y-6"
          >
            <Badge
              variant="outline"
              className="mb-6 px-4 py-1.5 text-sm border-primary/30 bg-primary/10 text-primary"
            >
              {t('badge')}
            </Badge>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground">
              {t.rich('title', {
                highlight: (chunks) => (
                  <span className="text-primary relative inline-block">
                    {chunks}
                    <svg
                      className="absolute w-full h-3 -bottom-1 left-0 text-primary/20 -z-10"
                      viewBox="0 0 100 10"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M0 5 Q 50 10 100 5"
                        stroke="currentColor"
                        strokeWidth="8"
                        fill="none"
                      />
                    </svg>
                  </span>
                ),
              })}
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {t('subtitle')}
            </p>
          </motion.header>

          {/* Benefits Grid with 3D Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto perspective-distant">
            {benefits.map((benefit, index) => (
              <BenefitCard3D key={benefit.key} benefit={benefit} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitCard3D({ benefit, index }: { benefit: BenefitItem; index: number }) {
  const t = useTranslations('landing.benefits');
  const Icon = benefit.icon;
  const cardRef = useRef<HTMLDivElement>(null);

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative"
    >
      <TiltCard>
        <div className="relative h-full bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-lg hover:shadow-2xl hover:border-primary/30 transition-all duration-500 overflow-hidden">
          {/* Animated Gradient Background */}
          <div
            className={cn(
              'absolute inset-0 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500',
              benefit.gradientFrom,
              benefit.gradientTo,
            )}
          />

          {/* Decorative Glow */}
          <div
            className={cn(
              'absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500',
              benefit.iconBg,
            )}
          />

          <div className="relative z-10 flex flex-col h-full space-y-6">
            {/* Icon and Metric Row */}
            <div className="flex items-start justify-between">
              <motion.div
                className={cn(
                  'p-4 rounded-xl transition-all duration-300 group-hover:scale-110',
                  benefit.iconBg,
                )}
                whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 0.5 }}
              >
                <Icon className={cn('h-7 w-7', benefit.iconColor)} />
              </motion.div>

              <div className="text-right">
                <motion.div
                  className="text-4xl md:text-5xl font-bold text-foreground"
                  initial={{ scale: 1 }}
                  whileInView={{ scale: [1, 1.1, 1] }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 + 0.3 }}
                >
                  {t(`items.${benefit.key}.metric`)}
                </motion.div>
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mt-1">
                  {t(`items.${benefit.key}.metricLabel`)}
                </div>
              </div>
            </div>

            {/* Title */}
            <h3 className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors duration-300">
              {t(`items.${benefit.key}.title`)}
            </h3>

            {/* Description */}
            <p className="text-base text-muted-foreground leading-relaxed flex-grow">
              {t(`items.${benefit.key}.description`)}
            </p>

            {/* Decorative Bottom Bar */}
            <div className="pt-4 border-t border-border/50">
              <div
                className={cn(
                  'h-1 rounded-full transition-all duration-500 w-0 group-hover:w-full',
                  benefit.iconColor.replace('text-', 'bg-'),
                )}
              />
            </div>
          </div>
        </div>
      </TiltCard>
    </motion.div>
  );
}

function TiltCard({ children }: { children: React.ReactNode }) {
  const mouseX = useRef(0);
  const mouseY = useRef(0);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;

    const rect = cardRef.current.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseXFromCenter = e.clientX - rect.left - width / 2;
    const mouseYFromCenter = e.clientY - rect.top - height / 2;

    mouseX.current = mouseXFromCenter / width;
    mouseY.current = mouseYFromCenter / height;

    const rotateX = mouseY.current * -10;
    const rotateY = mouseX.current * 10;

    if (cardRef.current) {
      cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
    }
  };

  const handleMouseLeave = () => {
    if (cardRef.current) {
      cardRef.current.style.transform =
        'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
    }
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="relative w-full h-full transition-transform duration-200 ease-out"
      style={{ transformStyle: 'preserve-3d' }}
    >
      {children}
    </div>
  );
}
